import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import Header from './Header'
import Footer from './Footer'
import DocumentMeta from './DocumentMeta.jsx'
import {
  PlayIcon,
  PauseIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline'
import { trackEvent } from './lib/analytics'

const DEFAULTS = {
  work: 25,
  short: 5,
  long: 15,
}

const MODES = {
  work: 'Μελέτη',
  short: 'Μικρό Διάλειμμα',
  long: 'Μεγάλο Διάλειμμα',
}

export default function PomodoroPage() {
  const location = useLocation()
  const [mode, setMode] = useState('work')
  const [cycle, setCycle] = useState(1)
  const [isRunning, setIsRunning] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(DEFAULTS.work * 60)
  const [durations, setDurations] = useState(DEFAULTS)
  const [showSettings, setShowSettings] = useState(false)

  const intervalRef = useRef(null)

  useEffect(() => {
    if (!isRunning) return

    clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current)
          handleSessionEnd()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(intervalRef.current)
  }, [isRunning, mode])

  const handleSessionEnd = () => {
    trackEvent('pomodoro_session_completed', {
      mode,                // what just finished
      cycle,
      duration_min: durations[mode],
    })
    
    let nextMode = 'work'
    let nextCycle = cycle
    let nextSeconds = durations.work * 60

    if (mode === 'work') {
      if (cycle === 4) {
        nextMode = 'long'
        nextCycle = 1
        nextSeconds = durations.long * 60
      } else {
        nextMode = 'short'
        nextCycle = cycle + 1
        nextSeconds = durations.short * 60
      }
    } else {
      nextMode = 'work'
      nextSeconds = durations.work * 60
    }

    setMode(nextMode)
    setCycle(nextCycle)
    setSecondsLeft(nextSeconds)
    setIsRunning(true)
  }

  const formatTime = s =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <DocumentMeta
        title="Pomodoro – Χρονόμετρο Μελέτης | Πανελλήνιες+"
        description="Pomodoro χρονόμετρο με κύκλους μελέτης/διαλείμματος και ρυθμίσεις διάρκειας. Ιδανικό για διάβασμα Πανελληνίων."
        canonical={`https://www.panellhniesplus.gr${location.pathname}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Pomodoro – Χρονόμετρο Μελέτης",
            url: `https://www.panellhniesplus.gr${location.pathname}`,
            applicationCategory: "EducationalApplication",
            operatingSystem: "Web",
          }),
        }}
      />
      <Header />

      <main className="max-w-xl mx-auto px-4 py-12">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border p-6 sm:p-8 text-center">

          {/* Title */}
          <h1 className="text-xl sm:text-2xl font-semibold">
            Χρονόμετρο Μελέτης
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {MODES[mode]} {mode === 'work' && `• Κύκλος ${cycle}/4`}
          </p>

          {/* Timer */}
          <div className="my-10">
            <div className="text-[clamp(3rem,14vw,5rem)] font-mono font-semibold tracking-tight">
              {formatTime(secondsLeft)}
            </div>
          </div>

          {/* Cycle dots */}
          <div className="flex justify-center gap-2 mb-8">
            {[1, 2, 3, 4].map(n => (
              <div
                key={n}
                className={`h-2.5 w-2.5 rounded-full ${
                  n < cycle || (n === cycle && mode === 'work')
                    ? 'bg-gray-900'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Controls */}
          <div className="flex justify-center gap-3 mb-6">
          <button
            onClick={() => {
              setIsRunning((p) => {
                const next = !p
            
                if (next) {
                  trackEvent('pomodoro_started', {
                    mode,
                    cycle,
                    duration_min: durations[mode],
                  })
                } else {
                  trackEvent('pomodoro_paused', {
                    mode,
                    cycle,
                    seconds_left: secondsLeft,
                  })
                }
            
                return next
              })
            }}            
            className="flex items-center gap-2 px-5 py-2.5 
                      bg-gray-900 hover:bg-gray-800 text-white 
                      rounded-lg transition cursor-pointer"
          >
              {isRunning ? <PauseIcon className="h-5" /> : <PlayIcon className="h-5" />}
              {isRunning ? 'Παύση' : 'Έναρξη'}
            </button>

            <button
              onClick={() => {
                trackEvent('pomodoro_reset', {
                  mode,
                  cycle,
                  seconds_left: secondsLeft,
                })
                setIsRunning(false)
                setMode('work')
                setCycle(1)
                setSecondsLeft(durations.work * 60)
              }}
              className="flex items-center gap-2 px-4 py-2.5 border rounded-lg text-gray-700 hover:bg-gray-100 transition cursor-pointer"
            >
              <ArrowPathIcon className="h-5" />
              Reset
            </button>
          </div>

          {/* Settings toggle */}
          <button
            onClick={() => setShowSettings(p => !p)}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-600 cursor-pointer"
          >
            <Cog6ToothIcon className="h-5" />
            Ρυθμίσεις
          </button>

          {/* Settings */}
          {showSettings && (
            <div className="mt-6 border-t pt-6 text-left grid grid-cols-1 sm:grid-cols-3 gap-4">
              {['work', 'short', 'long'].map(type => (
                <div key={type}>
                  <label className="text-xs text-gray-500">
                    {MODES[type]}
                  </label>
                  <input
                    type="number"
                    value={durations[type]}
                    min={1}
                    max={60}
                    onChange={e => {
                      const v = parseInt(e.target.value)
                      if (!v || v <= 0 || v > 60) return
                      setDurations(d => ({ ...d, [type]: v }))
                      if (mode === type) setSecondsLeft(v * 60)
                    }}
                    className="mt-1 w-full border rounded-md px-3 py-2
           focus:ring-2 focus:ring-gray-600 focus:outline-none"
/>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  )
}
